import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  ArrowLeft, Heart, ChatCircle, ShareFat, MusicNote, ShieldCheck,
  X as XIcon, CaretUp, CaretDown, ArrowSquareOut, DotsSixVertical,
  CheckCircle, XCircle, Warning, Question,
} from "@phosphor-icons/react";

import Header from "@/components/Header";
import VerdictBadge from "@/components/VerdictBadge";
import ConfidenceMeter from "@/components/ConfidenceMeter";
import SourceItem from "@/components/SourceItem";
import { getVerdictStyle } from "@/lib/verdictStyles";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mock TikTok/Reels feed items — with pre-baked YouTube URLs so the demo fact-checks are real
const FEED = [
  {
    id: "feed-1",
    author: "@stanford_official",
    caption: "Steve Jobs · 'Stay hungry, stay foolish' 🍎 #steve #inspiration #tech",
    music: "Original audio · Stanford",
    thumbnail: "https://i.ytimg.com/vi/UF8uR6Z6KLc/maxresdefault.jpg",
    videoUrl: "https://www.youtube.com/watch?v=UF8uR6Z6KLc",
    likes: "2.1M", comments: "18.4K", shares: "94K",
  },
  {
    id: "feed-2",
    author: "@jfk_archive",
    caption: "The oath of a president — 1961 🇺🇸 #jfk #history #politics",
    music: "Original audio · CBS News Archive",
    thumbnail: "https://i.ytimg.com/vi/PEC1C4p0k3E/maxresdefault.jpg",
    videoUrl: "https://www.youtube.com/watch?v=PEC1C4p0k3E",
    likes: "890K", comments: "4.2K", shares: "22K",
  },
];

// Bubble states
const STATE = { IDLE: "idle", LOADING: "loading", COMPACT: "compact", EXPANDED: "expanded" };

// Draggable bubble hook
function useDraggable(initial = { x: 20, y: 240 }) {
  const [pos, setPos] = useState(initial);
  const dragging = useRef(false);
  const start = useRef({ x: 0, y: 0, px: 0, py: 0 });

  const onPointerDown = (e) => {
    dragging.current = true;
    const p = "touches" in e ? e.touches[0] : e;
    start.current = { x: p.clientX, y: p.clientY, px: pos.x, py: pos.y };
    e.stopPropagation();
  };
  const onPointerMove = useCallback((e) => {
    if (!dragging.current) return;
    const p = "touches" in e ? e.touches[0] : e;
    const dx = p.clientX - start.current.x;
    const dy = p.clientY - start.current.y;
    setPos({ x: start.current.px + dx, y: start.current.py + dy });
  }, []);
  const onPointerUp = useCallback(() => {
    dragging.current = false;
  }, []);

  useEffect(() => {
    window.addEventListener("mousemove", onPointerMove);
    window.addEventListener("mouseup", onPointerUp);
    window.addEventListener("touchmove", onPointerMove, { passive: false });
    window.addEventListener("touchend", onPointerUp);
    return () => {
      window.removeEventListener("mousemove", onPointerMove);
      window.removeEventListener("mouseup", onPointerUp);
      window.removeEventListener("touchmove", onPointerMove);
      window.removeEventListener("touchend", onPointerUp);
    };
  }, [onPointerMove, onPointerUp]);

  return { pos, onPointerDown, setPos };
}

function VerdictIcon({ verdict, className }) {
  const map = { TRUE: CheckCircle, FALSE: XCircle, PARTIALLY_TRUE: Warning, UNVERIFIABLE: Question, OUTDATED: Warning, SATIRE: Warning };
  const Icon = map[verdict] || Question;
  return <Icon weight="fill" className={className} />;
}

// The actual bubble component ============================================
function OverlayBubble({ videoUrl, phoneRect, autoTrigger, onExit }) {
  const [state, setState] = useState(STATE.IDLE);
  const [progress, setProgress] = useState("Initializing");
  const [claims, setClaims] = useState([]);
  const [result, setResult] = useState(null);
  const { pos, onPointerDown, setPos } = useDraggable({ x: 20, y: 240 });
  const sourceRef = useRef(null);

  // Reset state when video changes
  useEffect(() => {
    setState(STATE.IDLE);
    setClaims([]);
    setResult(null);
    setProgress("Initializing");
    if (sourceRef.current) { sourceRef.current.close(); sourceRef.current = null; }
    setPos({ x: 20, y: 240 });
  }, [videoUrl, setPos]);

  const startCheck = useCallback(() => {
    if (!videoUrl) return;
    setState(STATE.LOADING);
    setClaims([]);
    setResult(null);
    setProgress("Connecting to pipeline");
    if (sourceRef.current) sourceRef.current.close();

    const params = new URLSearchParams({ video_url: videoUrl, platform: "youtube", force_refresh: "false" });
    const es = new EventSource(`${API}/v1/fact-check/stream?${params}`);
    sourceRef.current = es;
    const parse = (raw) => { try { return JSON.parse(raw); } catch { return null; } };

    es.addEventListener("status", (e) => { const d = parse(e.data); if (d) setProgress(d.message || d.stage); });
    es.addEventListener("transcript", () => setProgress("Detecting claims"));
    es.addEventListener("claims_detected", (e) => { const d = parse(e.data); if (d) setProgress(`Found ${d.count} claim${d.count === 1 ? "" : "s"}`); });
    es.addEventListener("claim_result", (e) => {
      const d = parse(e.data);
      if (d) setClaims((cs) => [...cs, d]);
      setProgress("Verifying");
    });
    const finish = (d) => {
      if (d) setResult(d);
      setState(STATE.COMPACT);
      es.close();
      sourceRef.current = null;
    };
    es.addEventListener("cache_hit", (e) => finish(parse(e.data)));
    es.addEventListener("done", (e) => finish(parse(e.data)));
    es.addEventListener("error", (e) => {
      const d = parse(e.data);
      setProgress(d?.message || "Pipeline halted");
      setState(STATE.COMPACT);
      es.close();
    });
  }, [videoUrl]);

  // Auto-trigger fact-check when user swipes to a new video (simulates AccessibilityService detection)
  useEffect(() => {
    if (autoTrigger && videoUrl) {
      const t = setTimeout(startCheck, 800);
      return () => clearTimeout(t);
    }
  }, [autoTrigger, videoUrl, startCheck]);

  const verdict = result?.overall_verdict;
  const style = verdict ? getVerdictStyle(verdict) : null;

  // Constrain position to phone screen
  const maxX = (phoneRect?.width || 375) - 60;
  const maxY = (phoneRect?.height || 720) - 60;
  const clampedX = Math.max(4, Math.min(pos.x, maxX));
  const clampedY = Math.max(60, Math.min(pos.y, maxY));

  return (
    <>
      {/* Idle / Loading / Compact — floating bubble/card at absolute position */}
      <AnimatePresence>
        {state !== STATE.EXPANDED && (
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.6 }}
            style={{ left: clampedX, top: clampedY, position: "absolute", zIndex: 40 }}
            data-testid="bubble-container"
          >
            {state === STATE.IDLE && (
              <button
                onMouseDown={onPointerDown}
                onTouchStart={onPointerDown}
                onClick={startCheck}
                data-testid="bubble-idle"
                className="w-14 h-14 rounded-full bg-black text-white flex items-center justify-center shadow-[4px_4px_0px_rgba(0,0,0,0.4)] cursor-grab active:cursor-grabbing hover:scale-105 transition-transform"
                title="Tap to fact-check"
              >
                <ShieldCheck weight="fill" className="w-6 h-6 text-emerald-400" />
              </button>
            )}

            {state === STATE.LOADING && (
              <div
                onMouseDown={onPointerDown}
                onTouchStart={onPointerDown}
                data-testid="bubble-loading"
                className="w-24 h-24 rounded-full bg-black text-white flex flex-col items-center justify-center relative cursor-grab shadow-[4px_4px_0px_rgba(0,0,0,0.4)]"
              >
                <div className="absolute inset-0 rounded-full border-2 border-emerald-400 border-t-transparent animate-spin" />
                <ShieldCheck weight="fill" className="w-5 h-5 text-emerald-400 mb-1" />
                <span className="font-mono-plex text-[7px] font-semibold uppercase tracking-wider">
                  {claims.length ? `${claims.length}/${claims.length}` : "SCAN"}
                </span>
              </div>
            )}

            {state === STATE.COMPACT && result && (
              <div
                data-testid="bubble-compact"
                className="w-[300px] border-2 border-black bg-white shadow-[4px_4px_0px_rgba(0,0,0,1)]"
              >
                {/* Drag handle strip */}
                <div
                  onMouseDown={onPointerDown}
                  onTouchStart={onPointerDown}
                  className="flex items-center justify-between px-2 py-1 bg-black text-white cursor-grab"
                >
                  <span className="flex items-center gap-1.5 font-mono-plex text-[9px] font-semibold">
                    <DotsSixVertical weight="bold" className="w-3 h-3" />
                    FACTCHECKAI
                  </span>
                  <button
                    onClick={() => { setState(STATE.IDLE); onExit?.(); }}
                    className="text-zinc-400 hover:text-white"
                    data-testid="bubble-dismiss"
                  >
                    <XIcon weight="bold" className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Card body */}
                <div className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className={`flex items-center gap-1.5 font-mono-plex text-[10px] font-semibold px-2 py-0.5 border ${style?.border} ${style?.bg} ${style?.text}`}>
                      <VerdictIcon verdict={verdict} className={`w-3 h-3 ${style?.iconColor}`} />
                      {style?.label}
                    </div>
                    <div className="font-mono-plex text-[9px] font-semibold text-zinc-500">
                      {result.cached ? "CACHED · <500ms" : `${(result.processing_time_ms / 1000).toFixed(1)}s`}
                    </div>
                  </div>

                  {/* Confidence */}
                  <div className="mb-2">
                    <ConfidenceMeter value={result.overall_confidence || 0} size="sm" />
                  </div>

                  {/* Summary */}
                  <p className="text-[11px] text-zinc-700 leading-snug mb-3 line-clamp-2">
                    {result.overall_summary}
                  </p>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 border-t border-zinc-200 pt-2">
                    <button
                      onClick={() => setState(STATE.EXPANDED)}
                      data-testid="bubble-expand"
                      className="flex-1 flex items-center justify-center gap-1 bg-black text-white px-2 py-1.5 font-mono-plex text-[9px] font-semibold hover:bg-zinc-800"
                    >
                      <CaretUp weight="bold" className="w-3 h-3" />
                      EXPAND · {result.claims?.length || 0} CLAIMS
                    </button>
                    <a
                      href={`/check/${result.cache_key}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="border border-zinc-400 px-2 py-1.5 font-mono-plex text-[9px] font-semibold text-zinc-700 hover:bg-zinc-100 flex items-center gap-1"
                    >
                      <ArrowSquareOut weight="bold" className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Expanded — full sheet, still inside the phone frame */}
      <AnimatePresence>
        {state === STATE.EXPANDED && result && (
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 32 }}
            data-testid="bubble-expanded"
            className="absolute inset-x-0 bottom-0 top-10 bg-[#F9F9F8] border-t-2 border-black overflow-y-auto z-50"
          >
            {/* Sheet header */}
            <div className="sticky top-0 bg-black text-white px-4 py-3 flex items-center justify-between z-10">
              <div>
                <div className="font-mono-plex text-[9px] font-semibold text-emerald-400">
                  FACTCHECKAI · IN-APP SHEET
                </div>
                <div className="font-display text-lg leading-none mt-0.5">
                  {style?.label} · {Math.round((result.overall_confidence || 0) * 100)}%
                </div>
              </div>
              <button
                onClick={() => setState(STATE.COMPACT)}
                data-testid="bubble-collapse"
                className="border border-white/40 p-1.5 hover:bg-white/10"
              >
                <CaretDown weight="bold" className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4">
              {/* Video meta */}
              <div className="flex items-start gap-3 mb-4 border-b border-zinc-200 pb-4">
                <img
                  src={result.video_metadata?.thumbnail_url}
                  alt=""
                  className="w-24 h-16 object-cover border border-zinc-300"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-mono-plex text-[9px] font-semibold text-zinc-500 mb-1">
                    {result.video_metadata?.channel}
                  </div>
                  <div className="font-display text-sm text-zinc-950 leading-tight line-clamp-2">
                    {result.video_metadata?.title}
                  </div>
                </div>
              </div>

              {/* Overall summary */}
              <p className="text-xs text-zinc-700 mb-4 leading-relaxed">
                {result.overall_summary}
              </p>

              {/* Claim list — compact */}
              <div className="space-y-3">
                {(result.claims || []).map((c, i) => {
                  const s = getVerdictStyle(c.verdict);
                  return (
                    <div key={c.claim_id} className="border border-zinc-300 bg-white p-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="font-mono-plex text-[9px] font-semibold text-zinc-500">
                          CLAIM {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className={`font-mono-plex text-[9px] font-semibold px-1.5 py-0.5 border ${s.border} ${s.bg} ${s.text}`}>
                          {s.label} · {Math.round(c.confidence * 100)}%
                        </span>
                      </div>
                      <p className="font-display text-[13px] text-zinc-950 leading-snug mb-1.5 line-clamp-3">
                        &ldquo;{c.original_quote}&rdquo;
                      </p>
                      {c.short_explanation && (
                        <p className="text-[11px] text-zinc-600 leading-snug border-l-2 border-zinc-900 pl-2 line-clamp-3">
                          {c.short_explanation}
                        </p>
                      )}
                      <div className="mt-2 font-mono-plex text-[9px] text-zinc-500">
                        {c.sources?.length || 0} SOURCES
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Deep-link out */}
              <a
                href={`/check/${result.cache_key}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="bubble-open-full"
                className="mt-4 flex items-center justify-center gap-1.5 bg-black text-white py-2.5 font-mono-plex text-[10px] font-semibold hover:bg-zinc-800"
              >
                <ArrowSquareOut weight="bold" className="w-3.5 h-3.5" />
                OPEN FULL REPORT IN FACTCHECKAI
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// The fake TikTok/Reels feed on the "phone" ==============================
function FakeVideoFeed({ index, feed }) {
  const item = feed[index];
  return (
    <div className="absolute inset-0 bg-black overflow-hidden">
      <img
        src={item.thumbnail}
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-90"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40" />

      {/* Right-side action rail (TikTok-style) */}
      <div className="absolute right-3 bottom-24 flex flex-col items-center gap-5 text-white">
        <div className="flex flex-col items-center">
          <div className="w-11 h-11 rounded-full bg-white/20 border-2 border-white flex items-center justify-center">
            <span className="font-display text-sm">JA</span>
          </div>
          <div className="w-5 h-5 bg-red-500 rounded-full -mt-2 flex items-center justify-center font-mono text-[10px] font-bold">+</div>
        </div>
        <div className="flex flex-col items-center">
          <Heart weight="fill" className="w-8 h-8" />
          <span className="font-mono-plex text-[9px] font-semibold mt-0.5">{item.likes}</span>
        </div>
        <div className="flex flex-col items-center">
          <ChatCircle weight="fill" className="w-8 h-8" />
          <span className="font-mono-plex text-[9px] font-semibold mt-0.5">{item.comments}</span>
        </div>
        <div className="flex flex-col items-center">
          <ShareFat weight="fill" className="w-8 h-8" />
          <span className="font-mono-plex text-[9px] font-semibold mt-0.5">{item.shares}</span>
        </div>
      </div>

      {/* Bottom caption */}
      <div className="absolute left-3 right-16 bottom-6 text-white">
        <div className="font-display text-lg font-semibold mb-1.5">{item.author}</div>
        <p className="text-[13px] leading-snug mb-2 line-clamp-3">{item.caption}</p>
        <div className="flex items-center gap-1.5 text-[11px] opacity-90">
          <MusicNote weight="fill" className="w-3 h-3" />
          <span className="line-clamp-1">{item.music}</span>
        </div>
      </div>

      {/* Faux status bar */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between text-white text-[10px] font-mono px-3 py-1 bg-gradient-to-b from-black/60 to-transparent">
        <span>19:32</span>
        <span>● ● ● 4G</span>
      </div>
    </div>
  );
}

// Page ==================================================================
export default function BubblePreviewPage() {
  const [feedIndex, setFeedIndex] = useState(0);
  const [autoTrigger, setAutoTrigger] = useState(false);
  const phoneRef = useRef(null);
  const [phoneRect, setPhoneRect] = useState({ width: 375, height: 720 });

  useEffect(() => {
    if (phoneRef.current) {
      const r = phoneRef.current.getBoundingClientRect();
      setPhoneRect({ width: r.width, height: r.height });
    }
  }, []);

  const swipe = (dir) => {
    const next = feedIndex + dir;
    if (next >= 0 && next < FEED.length) setFeedIndex(next);
  };

  const currentItem = FEED[feedIndex];

  return (
    <div className="min-h-screen w-full flex flex-col text-zinc-900 relative">
      <Header />

      <section className="max-w-[1600px] mx-auto w-full px-6 md:px-12 pt-10 pb-4 relative z-10">
        <Link
          to="/"
          data-testid="back-to-home"
          className="inline-flex items-center gap-2 font-mono-plex text-[10px] font-semibold text-zinc-700 hover:text-zinc-950 mb-6"
        >
          <ArrowLeft weight="bold" className="w-3.5 h-3.5" />
          BACK TO FACT-CHECKER
        </Link>
        <div className="font-mono-plex text-[10px] font-semibold text-emerald-700 mb-3">
          ANDROID OVERLAY PROTOTYPE · INTERACTIVE MOCKUP
        </div>
        <h1 className="font-display text-4xl md:text-6xl leading-tight tracking-tight text-zinc-950 mb-4">
          The bubble, before we build it.
        </h1>
        <p className="text-zinc-700 text-lg max-w-2xl leading-relaxed">
          This is what the Android overlay would feel like on TikTok, Reels, and Shorts.
          Drag the bubble. Tap it to fact-check the current fake feed video (real backend, real
          verdicts). Expand for details — the user never actually leaves the video app.
        </p>
      </section>

      <section className="max-w-[1600px] mx-auto w-full px-6 md:px-12 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-8 relative z-10">
        {/* Left: fake phone with feed + bubble */}
        <div className="lg:col-span-5 flex flex-col items-center">
          <div className="relative">
            {/* Phone frame */}
            <div className="bg-black rounded-[2.5rem] p-3 shadow-[10px_10px_0px_rgba(0,0,0,0.9)]">
              <div
                ref={phoneRef}
                data-testid="phone-frame"
                className="relative bg-black rounded-[2rem] overflow-hidden"
                style={{ width: 375, height: 720 }}
              >
                <FakeVideoFeed index={feedIndex} feed={FEED} />
                <OverlayBubble
                  key={currentItem.id + (autoTrigger ? "-auto" : "")}
                  videoUrl={currentItem.videoUrl}
                  phoneRect={phoneRect}
                  autoTrigger={autoTrigger}
                />
              </div>
            </div>
          </div>

          {/* Feed navigation */}
          <div className="mt-6 flex items-center gap-3">
            <button
              onClick={() => swipe(-1)}
              disabled={feedIndex === 0}
              data-testid="feed-prev"
              className="border-2 border-black px-3 py-2 font-mono-plex text-[10px] font-semibold hover:bg-black hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              ← PREV VIDEO
            </button>
            <span className="font-mono-plex text-[10px] text-zinc-500 font-semibold">
              {feedIndex + 1} / {FEED.length}
            </span>
            <button
              onClick={() => swipe(1)}
              disabled={feedIndex === FEED.length - 1}
              data-testid="feed-next"
              className="border-2 border-black px-3 py-2 font-mono-plex text-[10px] font-semibold hover:bg-black hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              NEXT VIDEO →
            </button>
          </div>

          <label className="mt-3 flex items-center gap-2 font-mono-plex text-[10px] text-zinc-700 font-semibold cursor-pointer">
            <input
              type="checkbox"
              checked={autoTrigger}
              onChange={(e) => setAutoTrigger(e.target.checked)}
              data-testid="auto-trigger-toggle"
              className="w-3.5 h-3.5"
            />
            AUTO-CHECK EVERY VIDEO (SIMULATES ACCESSIBILITY MODE)
          </label>
        </div>

        {/* Right: explainer */}
        <aside className="lg:col-span-7 space-y-6">
          <div className="border-2 border-black bg-white p-5">
            <div className="font-mono-plex text-[10px] font-semibold text-emerald-700 mb-2">
              WHAT YOU&apos;RE LOOKING AT
            </div>
            <h3 className="font-display text-2xl text-zinc-950 mb-3">A working prototype, not a screenshot.</h3>
            <ul className="space-y-2 text-[14px] text-zinc-700 leading-relaxed">
              <li className="flex gap-2"><span className="font-mono-plex text-emerald-700 text-[11px] mt-1">01.</span><span>Tap the black bubble to trigger a real fact-check against our SSE backend</span></li>
              <li className="flex gap-2"><span className="font-mono-plex text-emerald-700 text-[11px] mt-1">02.</span><span>Watch it transition through <b>Idle → Loading (with spinner) → Compact Result → Expanded Sheet</b></span></li>
              <li className="flex gap-2"><span className="font-mono-plex text-emerald-700 text-[11px] mt-1">03.</span><span>Drag the bubble anywhere on the phone screen — this is exactly how it&apos;ll behave in the Kotlin build</span></li>
              <li className="flex gap-2"><span className="font-mono-plex text-emerald-700 text-[11px] mt-1">04.</span><span>Swipe to next video and toggle <b>Auto-check</b> to feel what &ldquo;accessibility mode&rdquo; would do</span></li>
              <li className="flex gap-2"><span className="font-mono-plex text-emerald-700 text-[11px] mt-1">05.</span><span>Expanded sheet stays <b>inside</b> the phone frame — the user is still &ldquo;in TikTok&rdquo;</span></li>
            </ul>
          </div>

          <div className="border border-zinc-300 bg-white p-5">
            <div className="font-mono-plex text-[10px] font-semibold text-zinc-500 mb-2">
              HONEST CONSTRAINTS
            </div>
            <h4 className="font-display text-lg text-zinc-950 mb-2">What the real Android app cannot do (that this prototype fakes)</h4>
            <p className="text-[13px] text-zinc-700 leading-relaxed mb-2">
              This prototype knows the video URL because we hard-coded it. The real Kotlin app has to
              acquire it via one of: <b>Share Intent</b> (100% reliable), <b>Clipboard scrape</b> (needs
              user permission), or <b>AccessibilityService</b> (Play-Store risky). We recommend
              shipping <b>Share + Clipboard only</b> in v1.0 and adding accessibility mode after we
              earn a track record.
            </p>
            <p className="text-[13px] text-zinc-700 leading-relaxed">
              See <code className="bg-zinc-100 px-1 py-0.5 text-[12px]">/app/memory/ANDROID_OVERLAY_BUBBLE.md</code> for
              the full architecture doc, Kotlin code stubs, and Play Store risk matrix.
            </p>
          </div>

          <div className="border border-zinc-300 bg-white p-5">
            <div className="font-mono-plex text-[10px] font-semibold text-zinc-500 mb-2">
              BACKEND WORK REQUIRED
            </div>
            <h4 className="font-display text-lg text-zinc-950 mb-2">Minimal — almost all reusable</h4>
            <ul className="space-y-1.5 text-[13px] text-zinc-700 leading-relaxed">
              <li>✅ <code className="bg-zinc-100 px-1">POST /api/v1/fact-check</code> — instant cached result</li>
              <li>✅ <code className="bg-zinc-100 px-1">GET /api/v1/fact-check/stream</code> — SSE (OkHttp EventSource on Android)</li>
              <li>✅ <code className="bg-zinc-100 px-1">GET /api/v1/fact-check/{"{cache_key}"}</code> — WebView hydration</li>
              <li>⏳ Add <code className="bg-zinc-100 px-1">?embed=1</code> query param to hide site chrome in WebView</li>
              <li>⏳ <code className="bg-zinc-100 px-1">/.well-known/assetlinks.json</code> for App Links (deep-linking into the app)</li>
            </ul>
          </div>
        </aside>
      </section>
    </div>
  );
}
